
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AnimalMap from '../components/AnimalMap';
import { listsApi } from '../utils/listsApi';
import './AnimalPage.css';

const API_URL = (
  process.env.REACT_APP_API_URL || ''
).replace(/\/+$/, '');

function getImageUrl(path) {
  if (!path) return null;

  if (/^https?:\/\//i.test(path)) return path;

  return `${API_URL}/${path.replace(/^\/+/, '')}`;
}

function AnimalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [animal, setAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageFailed, setImageFailed] = useState(false);

  // User's collections
  const [lists, setLists] = useState([]);
  const [listIds, setListIds] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);

  const [membershipLoading, setMembershipLoading] = useState(true);
  const [membershipError, setMembershipError] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  // Load the animal's information
  useEffect(() => {
    let active = true;

    setLoading(true);
    setAnimal(null);
    setImageFailed(false);
    setError('');
    setActionError('');

    async function loadAnimal() {
      try {
        const response = await fetch(`${API_URL}/animals/${id}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Unable to load animal.');
        }

        if (active) setAnimal(data);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadAnimal();

    return () => {
      active = false;
    };
  }, [id]);

  // Load available lists and animal memberships
  useEffect(() => {
    if (!token) {
      setMembershipLoading(false);
      return;
    }

    let active = true;

    setMembershipLoading(true);
    setMembershipError('');

    async function loadMemberships() {
      try {
        const [availableLists, membership] = await Promise.all([
          listsApi('/lists'),
          listsApi(`/lists/animal/${id}/membership`)
        ]);

        if (!active) return;

        setLists(availableLists);
        setListIds(membership.list_ids.map(String));
        setIsFavorite(membership.is_favorite);

      } catch (err) {
        if (active) setMembershipError(err.message);
      } finally {
        if (active) setMembershipLoading(false);
      }
    }

    loadMemberships();

    return () => {
      active = false;
    };
  }, [id, token]);

  // Add or remove from Favorites
  async function handleToggleFavorite() {
    if (!animal || busy) return;

    setBusy(true);
    setActionError('');

    try {
      const response = await fetch(
        isFavorite
          ? `${API_URL}/favorites/${animal.id}`
          : `${API_URL}/favorites`,
        {
          method: isFavorite ? 'DELETE' : 'POST',
          headers: {
            token,
            'Content-Type': 'application/json'
          },
          ...(!isFavorite
            ? { body: JSON.stringify({ animal_id: animal.id }) }
            : {})
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || 'Unable to update Favorites.'
        );
      }

      // Update the button without displaying a message
      setIsFavorite(previous => !previous);

    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Add or remove an animal from a custom list
  async function handleToggleList(listId) {
    if (!animal || busy) return;

    const key = String(listId);
    const alreadyAdded = listIds.includes(key);

    setBusy(true);
    setActionError('');

    try {
      if (alreadyAdded) {
        await listsApi(
          `/lists/${key}/animals/${animal.id}`,
          { method: 'DELETE' }
        );
      } else {
        await listsApi(`/lists/${key}/animals`, {
          method: 'POST',
          body: { animal_id: animal.id }
        });
      }

      // Update the checkbox without displaying a message
      setListIds(previous =>
        alreadyAdded
          ? previous.filter(value => value !== key)
          : [...previous, key]
      );

    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Return to the previous page
  function goBack() {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }

  if (loading) {
    return (
      <div className="animal-page">
        Loading animal...
      </div>
    );
  }

  if (error || !animal) {
    return (
      <div className="animal-page">
        <p role="alert">
          {error || 'Animal not found.'}
        </p>

        <button onClick={goBack}>
          ← Back
        </button>
      </div>
    );
  }

  const imageUrl = getImageUrl(animal.image_path);

  return (
    <main className="animal-page">

      {/* Back button */}
      <button
        type="button"
        className="animal-back-btn"
        onClick={goBack}
      >
        ← Back
      </button>

      {/* Animal name */}
      <h1>{animal.name || animal.sci_name}</h1>

      {animal.sci_name && (
        <p className="animal-scientific-name">
          <em>{animal.sci_name}</em>
        </p>
      )}

      {/* Image */}
      <div className="animal-photo">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={animal.name || animal.sci_name}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="animal-no-image">
            No image available
          </div>
        )}
      </div>

      {/* Description */}
      <p className="animal-description">
        {animal.description || 'Description not available yet.'}
      </p>

      {/* Favorites and custom lists */}
      {token ? (
        membershipLoading ? (
          <p>Loading your collections...</p>
        ) : membershipError ? (
          <p role="alert">
            {membershipError}
          </p>
        ) : (
          <div className="animal-collection-actions">

            {/* Favorites button */}
            <button
              type="button"
              className="favorite-btn"
              onClick={handleToggleFavorite}
              disabled={busy}
            >
              {isFavorite
                ? '♥ Remove from Favorites'
                : '♡ Add to Favorites'}
            </button>

            {/* Custom lists dropdown */}
            <details className="animal-list-dropdown">

              <summary>
                Manage Lists ({listIds.length})
              </summary>

              <div className="animal-list-options">
                {lists.length === 0 ? (
                  <p>
                    No custom lists yet.{' '}
                    <Link to="/lists">
                      Create a list
                    </Link>
                  </p>
                ) : (
                  lists.map(list => {
                    const checked = listIds.includes(
                      String(list.id)
                    );

                    return (
                      <label
                        className="animal-list-option"
                        key={list.id}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() =>
                            handleToggleList(list.id)
                          }
                        />

                        <span>{list.name}</span>
                      </label>
                    );
                  })
                )}
              </div>

            </details>
          </div>
        )
      ) : (
        <p>
          <Link to="/login">Log in</Link> to save this
          animal to Favorites or your lists.
        </p>
      )}

      {/* Only display errors, never success messages */}
      {actionError && (
        <p
          className="animal-action-error"
          role="alert"
        >
          {actionError}
        </p>
      )}

      {/* Expandable animal information */}
      <details className="animal-extra-info">

        <summary>
          More Animal Information
        </summary>

        <div className="animal-info-content">

          <p>
            <strong>Class:</strong> {animal.class || '—'}
          </p>

          <p>
            <strong>Order:</strong> {animal.animal_order || '—'}
          </p>

          <p>
            <strong>Family:</strong> {animal.family || '—'}
          </p>

          <p>
            <strong>Genus:</strong> {animal.genus || '—'}
          </p>

          {animal.habitat && (
            <p>
              <strong>Habitat:</strong>{' '}
              {Array.isArray(animal.habitat)
                ? animal.habitat.join(', ')
                : animal.habitat}
            </p>
          )}

          {animal.trophic_group && (
            <p>
              <strong>Trophic group:</strong> {animal.trophic_group}
            </p>
          )}

          {animal.weight != null && (
            <p>
              <strong>Weight:</strong> {animal.weight} kg
            </p>
          )}

          {animal.colour && (
            <p>
              <strong>Colour:</strong> {animal.colour}
            </p>
          )}

        </div>
      </details>

      {/* Geographic range */}
      {animal.range_map && (
        <section className="animal-range-section">

          <h2>Geographic Range</h2>

          <AnimalMap geojson={animal.range_map} />

        </section>
      )}

    </main>
  );
}

export default AnimalPage;
